//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import React from 'react';
import styled from 'styled-components';

import DescriptionAndValidationWrapper, {
  IDescriptionAndValidationProps,
} from '@components/molecules/description-and-validation-wrapper/DescriptionAndValidationWrapper';
import { useUniqueId } from '@hooks/useUniqueId';
import { Mixins } from '@theme/mixins';
import { ITrackableComponent, withTracking } from '@utils/withTracking';

interface IOwnProps {
  isDisabled?: boolean;
  // TODO DEV-60534: Require a label in all `TextInput` elements
  instructions?: string;
  onChange?: (value: string) => void;
  placeholder?: string;
  ref?: React.RefObject<HTMLInputElement>;
  value?: string;
}
type InputProps = React.DetailedHTMLProps<
  React.InputHTMLAttributes<HTMLInputElement>,
  HTMLInputElement
>;
export type Props = IOwnProps &
  IDescriptionAndValidationProps &
  ITrackableComponent &
  Omit<InputProps, 'onChange' | 'value' | 'ref'>;

/**
 * The `TextInput` atom renders a styled HTML input with a text label, and optional
 * error and help text for additional context.
 */
export const TextInput = React.forwardRef<HTMLInputElement, Props>(
  (
    {
      className = '',
      customLabel,
      errorText,
      formComponentId,
      hasError = false,
      helpText,
      instructions,
      isDisabled,
      label,
      labelHelpText,
      labelTextMixin,
      onChange = () => undefined,
      trackingId,
      value = '',
      ...inputProps
    },
    ref,
  ) => {
    const inputId = useUniqueId('text-input-label-id');

    const renderInput = (descriptionTextId?: string) => (
      <Input
        {...inputProps}
        ref={ref}
        aria-describedby={descriptionTextId}
        aria-invalid={hasError}
        disabled={isDisabled}
        hasError={hasError}
        id={formComponentId ?? inputId}
        onChange={(e) => onChange(e.target.value)}
        trackingId={trackingId}
        type='text'
        value={value}
      />
    );

    return (
      <DescriptionAndValidationWrapper
        className={className}
        customLabel={customLabel}
        errorText={errorText}
        formComponentId={formComponentId ?? inputId}
        hasError={hasError}
        helpText={helpText}
        instructions={instructions}
        label={label}
        labelHelpText={labelHelpText}
        labelTextMixin={labelTextMixin}
        renderFormComponent={renderInput}
      />
    );
  },
);

export const Input = withTracking(
  styled.input<{ hasError: boolean }>(
    ({ hasError, theme }) => `
  ${hasError ? Mixins.FormControl.Error : Mixins.FormControl.Primary}
  width: 100%;
  border-radius: ${theme.spacings[1]};
`,
  ),
);

TextInput.displayName = 'TextInput';

export default TextInput;
